if (!customElements.get('soothara-hero')) {
  customElements.define('soothara-hero', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.dots = [...this.querySelectorAll('[data-go]')];
      this.toggle = this.querySelector('[data-toggle]');
      this.track = this.querySelector('.soothara-hero__slides');
      this.video = this.querySelector('.soothara-hero__video');
      this.index = 0;
      this.hovered = false;
      this.focused = false;
      this.editorSelected = false;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 749px)');
      this.userPaused = this.dataset.autoplay !== 'true' || this.motion.matches;
      this.interval = Math.max(4000, Number(this.dataset.interval) || 8000);
      this.dataset.enhanced = 'true';
      const listen = (target, event, fn, options = {}) => target.addEventListener(event, fn, {...options, signal});
      this.dots.forEach((dot, i) => listen(dot, 'click', () => this.show(i)));
      const togglePlayback = () => {
        this.userPaused = !this.userPaused;
        this.updateControl();
        this.schedule();
      };
      if (this.toggle) listen(this.toggle, 'click', togglePlayback);
      if (this.video) {
        this.video.controls = false;
        this.video.tabIndex = 0;
        this.video.setAttribute('role', 'button');
        const syncPlaybackState = () => {
          this.video.setAttribute('aria-pressed', String(!this.video.paused));
          if (this.toggle) this.video.setAttribute('aria-label', this.video.paused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel);
        };
        listen(this.video, 'click', togglePlayback);
        listen(this.video, 'keydown', e => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          togglePlayback();
        });
        listen(this.video, 'play', syncPlaybackState);
        listen(this.video, 'pause', syncPlaybackState);
        syncPlaybackState();
      }
      listen(this, 'pointerenter', e => { if (e.pointerType === 'mouse') {this.hovered = true; this.schedule();} });
      listen(this, 'pointerleave', e => { if (e.pointerType === 'mouse') {this.hovered = false; this.schedule();} });
      listen(this, 'focusin', () => {this.focused = true; this.schedule();});
      listen(this, 'focusout', e => {this.focused = this.contains(e.relatedTarget); this.schedule();});
      listen(this, 'keydown', e => {
        if (!e.target.closest('.soothara-hero__controls')) return;
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        this.show(this.index + (e.key === 'ArrowRight' ? 1 : -1));
        this.dots[this.index].focus();
      });
      listen(this, 'pointerdown', e => {
        if (e.pointerType === 'mouse' || e.target.closest('a,button')) return;
        this.swipe = {x:e.clientX,y:e.clientY,id:e.pointerId};
      }, {passive:true});
      listen(this, 'pointerup', e => {
        const start = this.swipe;
        this.swipe = null;
        if (!start || start.id !== e.pointerId) return;
        const dx = e.clientX - start.x;
        const dy = e.clientY - start.y;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) this.show(this.index + (dx < 0 ? 1 : -1));
      }, {passive:true});
      listen(this, 'pointercancel', () => {this.swipe = null;}, {passive:true});
      listen(document, 'visibilitychange', () => this.schedule());
      listen(this.mobile, 'change', () => {
        if (this.video && this.mobile.matches) this.show(0);
        else this.schedule();
      });
      listen(this.motion, 'change', e => {if(e.matches) this.userPaused = true; this.updateControl(); this.schedule();});
      listen(document, 'shopify:block:select', e => {
        const i = this.slides.findIndex(slide => slide.dataset.blockId === e.detail.blockId);
        if (i < 0) return;
        this.editorSelected = true;
        this.show(i);
      });
      listen(document, 'shopify:block:deselect', e => {
        if (!this.slides.some(slide => slide.dataset.blockId === e.detail.blockId)) return;
        this.editorSelected = false;
        this.schedule();
      });
      listen(document, 'shopify:section:select', e => {
        if (e.detail.sectionId !== this.dataset.sectionId) return;
        this.editorSelected = true;
        this.schedule();
      });
      listen(document, 'shopify:section:deselect', e => {
        if (e.detail.sectionId !== this.dataset.sectionId) return;
        this.editorSelected = false;
        this.schedule();
      });
      this.updateControl();
      this.show(0);
    }
    show(index) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.inert = !active;
        if (active) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden','true');
      });
      this.dots.forEach((dot, i) => {
        if (i === this.index) dot.setAttribute('aria-current','true');
        else dot.removeAttribute('aria-current');
      });
      this.schedule();
    }
    updateControl() {
      if (this.video) {
        if (this.userPaused) this.video.pause();
        else this.video.play().catch(() => {});
      }
      if (!this.toggle) return;
      this.toggle.setAttribute('aria-label', this.userPaused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel);
      this.toggle.querySelector('[data-play-icon]').hidden = !this.userPaused;
      this.toggle.querySelector('[data-pause-icon]').hidden = this.userPaused;
    }
    schedule() {
      clearTimeout(this.timer);
      const stopped = this.userPaused || this.hovered || this.focused || this.editorSelected || (this.video && this.mobile.matches) || document.hidden;
      this.track.setAttribute('aria-live', stopped ? 'polite' : 'off');
      if (stopped || this.slides.length < 2) return;
      this.timer = setTimeout(() => this.show(this.index + 1), this.interval);
    }
    disconnectedCallback() {
      this.video?.pause();
      clearTimeout(this.timer);
      this.controller?.abort();
      this.controller = null;
      delete this.dataset.enhanced;
    }
  });
}
